import { Form, Input, Button, Upload, Image } from "antd";
import { useCallback, useEffect, useState } from "react";
import type { FormInstance } from "antd";
import type { UploadFile, UploadProps } from "antd";
import { Customer } from "@lib/types/Customer";
import { fileToBase64, generateLogoFromText } from "@lib/helpers/FileHelper";
import { customerSchema as yupSchema } from "@lib/schemas/customer";
import { createYupSync } from "@lib/helpers/TableHelper";
import { BsCloudUpload } from "react-icons/bs";
import useIntl from "react-intl/lib/src/components/useIntl";

type Props = {
  form: FormInstance<Customer>;
  customer: Customer;
  initialValues: Customer;
};

const LogoSettings: React.FC<Props> = ({
  form,
  customer,
  initialValues: _initialValues,
}) => {
  const { messages } = useIntl();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const customerName = Form.useWatch("name", form);

  useEffect(() => {
    if (customer?.logoUrl) {
      setFileList([
        {
          uid: "-1",
          name: "image.png",
          status: "done",
          url: customer?.logoUrl,
          response: customer?.logoUrl,
        },
      ]);
    } else {
      setFileList([]);
    }
  }, [customer]);

  const onChange: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    if (newFileList.length > 0) {
      setFileList([newFileList[newFileList.length - 1]]);
    } else {
      setFileList([]);
    }
  };

  const [previewImg, setPreviewImg] = useState<string>();
  const onPreview = async (file: UploadFile) => {
    let src = file.url as string;
    if (!src) {
      src = await fileToBase64(file.originFileObj as File);
    }
    setPreviewImg(src);
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const handleGenerateLogo = useCallback(() => {
    if (!customerName || customerName.trim().length === 0) {
      return;
    }

    const generatedLogo = generateLogoFromText(customerName);
    if (generatedLogo) {
      form.setFieldValue("logoUrl", generatedLogo);
      setFileList([
        {
          uid: "-1",
          name: "generated-logo.png",
          status: "done",
          url: generatedLogo,
          response: generatedLogo,
        },
      ]);
    }
  }, [customerName, form]);

  return (
    <div style={{ padding: "24px" }}>
      <Form.Item
        label={messages["common.logo"] as string}
        valuePropName="fileList"
        getValueFromEvent={normFile}
        name="logoUrl"
        rules={[createYupSync({ schema: yupSchema })]}
      >
        <Input type="hidden" />
        <Upload
          listType="picture-card"
          multiple={false}
          onChange={onChange}
          onPreview={onPreview}
          fileList={fileList}
          customRequest={async (props) => {
            const image = await fileToBase64(props.file as File);
            form.setFieldValue("logoUrl", image);
            props.onSuccess(image);
          }}
        >
          <button style={{ border: 0, background: "none" }} type="button">
            <BsCloudUpload />
            <div style={{ marginTop: 8 }}>
              {messages["common.upload"] as string}
            </div>
          </button>
        </Upload>

        <Image
          width={200}
          style={{ display: "none" }}
          src=""
          preview={{
            visible: !!previewImg,
            src: previewImg,
            onVisibleChange: () => {
              setPreviewImg("");
            },
          }}
        />
        <div style={{ marginTop: 8 }}>
          <Button
            type="default"
            onClick={handleGenerateLogo}
            disabled={!customerName || customerName.trim().length === 0}
          >
            {(messages["common.generateLogo"] as string) || "Generate Logo"}
          </Button>
        </div>
      </Form.Item>
    </div>
  );
};

export default LogoSettings;
